import * as PIXI from 'pixi.js'
import Vue from 'vue'
import Detector from 'utils/Detector'
import * as CSS from 'utils/css.js'
import * as Utils from 'utils/math.js'
import * as Canvas from 'utils/canvas.js'
import SimplexNoise from 'simplex-noise'
import Emitter from 'core/Emitter'

import Cover from './Cover'
import Filters from './Filters'
import HomeFooter from './HomeFooter'
import Flower from 'components/Flower'
import FlowerParticles from 'components/FlowerParticles'

import Gui from 'core/Gui'

export default class Scene {
  constructor (opts = {}) {
    this.container = opts.container
    this.prefix = CSS.getPrefix()
    this.size = Detector.getScreenSize()
    this.simplex = new SimplexNoise()
    this.scrollY = {
      dest: 0,
      current: 0
    }

    this.params = {
      alphaMask: {
        value: 1,
        range: [0, 1]
      },
      debug: false,
      toggleMask: false,
      toggleBackground: false
    }
    this.wind = {
      force: {
        value: 5,
        range: [-45, 90]
      },
      speed: {
        value: 1,
        range: [-5, 10]
      }
    }

    this.createGrid()
    this.toggleDebugMode()

    // regular scene creation
    this.scene = new PIXI.Container()

    let pixelRatio = window.devicePixelRatio || 1

    PIXI.settings.RESOLUTION = pixelRatio
    PIXI.settings.RENDER_OPTIONS.autoResize = false

    let noWebgl = Detector.isWindow7()
    // eslint-disable-next-line new-cap
    this.renderer = new PIXI.autoDetectRenderer(this.size.w, this.size.h, {
      transparent: true,
      autoResize: false,
      resolution: pixelRatio
    }, noWebgl)

    this.renderer.isCanvas = (this.renderer instanceof PIXI.CanvasRenderer)

    this.renderer.plugins.interaction.autoPreventDefault = false
    this.renderer.plugins.interaction.interactionFrequency = 1000
    this.renderer.view.style['-ms-content-zooming'] = 'auto'
    this.renderer.view.style['-ms-touch-action'] = 'auto'
    this.renderer.view.style['touch-action'] = 'auto'

    this.container.appendChild(this.renderer.view)

    this.resources = window.loaderManager.resources
    this.flowersData = this.resources['flowers'].data

    Vue.nextTick(() => {
      this.init()
    })
  }

  bindAll () {
    ['getFilteredItems', 'resetFilter']
        .forEach((fn) => (this[fn] = this[fn].bind(this)))
  }

  addListeners () {
    Emitter.on('CLICK_FILTER', this.getFilteredItems)
    Emitter.on('FILTERS:RESET', this.resetFilter)
  }

  removeListeners () {
    Emitter.removeListener('CLICK_FILTER', this.getFilteredItems)
    Emitter.removeListener('FILTERS:RESET', this.resetFilter)
  }

  init () {
    this.bindAll()
    this.addListeners()
    this.createCover()
    this.createFilters()
    this.createHomeFooter()
    this.createFlowers()
  }

  createGrid () {
    this.grid = {
      row: 1,
      itemsByRow: () => {
        switch (this.size.BP) {
          case 'mobile':
          case 'xs-tablet':
            return 1
          case 'tablet':
            return 3
          default:
            return 5
        }
      },
      column: () => {
        switch (this.size.BP) {
          case 'mobile':
          case 'xs-tablet':
            return 1
          case 'tablet':
            return 2
          default:
            return 3
        }
      },
      maxWidth: 1260,
      maxHeight: () => {
        switch (this.size.BP) {
          case 'mobile':
          case 'xs-tablet':
            return this.size.h * 0.7
          case 'tablet':
            return this.size.h * 0.8
          default:
            return 850
        }
        // return this.size.w > 1280 ? 850 : this.size.h * 0.5
      },
      startTop: 0
    }
  }

  // return this.size.w > 1280 ? 3 : 2
  // return this.size.w > 1280 ? 5 : 3

  createCover () {
    this.coverComp = new Cover({
      resources: this.resources,
      scene: this.scene,
      renderer: this.renderer,
      size: this.size
    })
  }

  createHomeFooter () {
    this.homeFooterComp = new HomeFooter({
      size: this.size
    })
  }

  createFilters () {
    this.filtersComp = new Filters({
      resources: this.resources,
      size: this.size
    })
  }

  createParticles () {
    this.flowerParticles = new FlowerParticles({
      parent: this
    })
  }

  createFlowers () {
    let divFlowers = document.createElement('div')
    divFlowers.classList.add('flowers')
    this.container.appendChild(divFlowers)

    let index = 0

    this.flowers = []
    this.filteredFlowers = []
    this.flowersData.forEach((data) => {
      let flower = new Flower({
        index: index,
        resources: this.resources,
        data: data,
        scene: this.scene,
        renderer: this.renderer
      })

      this.flowers.push(flower)
      index++
    })

    // this.createParticles()

    this.flowersDiv = document.querySelector('.flowers')

    this.isReady = true
    this.resize(Detector.getScreenSize())
    // Emitter.emit('GET_RESIZE')

    // this.size = Detector.getScreenSize()

    // Emitter.emit('GET_RESIZE')
    Vue.nextTick(() => {
      if (this.goToFilters && window.APP_ALREADY_INIT) {
        setTimeout(() => {
          this.resetFilter()
        }, 1000)
      }
    })

    // this.gui()
  }

  getFilteredItems ({filterId}) {
    let scrollToItem = false
    let filteredItems = []
    this.flowers.forEach((flower, i) => {
      flower.desopacifyFlower()

      if (Utils.isInArray(flower.filtersArray, filterId)) {
        filteredItems.push(flower)
        // if (!this.scrollToItem) {
        //   this.scrollToItem = flower.y - (this.size.h) / 2
        // }
      } else {
        flower.opacifyFlower()
        // flower.flowerContainer.alpha = 0.3
      }
    })

    let mobile
    switch (this.size.BP) {
      case 'mobile':
      case 'xs-tablet':
        mobile = true
        break
      default:
        mobile = false
        break
    }

    if (mobile) {
      let flower = filteredItems[0]
      scrollToItem = flower.y - (this.size.h - flower.height) / 2
    } else {
      if (filteredItems.length > 1) {
        let flower1 = filteredItems[0]
        let flower2 = filteredItems[1]
        let top = (flower1.y > flower2.y) ? flower2.y : flower1.y
        let bottom
        let b1
        let b2
        let yDiff
        if (top === flower1.y) {
          b1 = top + flower1.height
          yDiff = flower2.y - top
          b2 = top + yDiff + flower2.height
        } else {
          b1 = top + flower2.height
          yDiff = flower1.y - top
          b2 = top + yDiff + flower1.height
        }

        bottom = (b1 > b2) ? b1 : b2
        let height = bottom - top

        scrollToItem = top - ((this.size.h - height) / 2)
      } else {
        let flower = filteredItems[0]
        scrollToItem = flower.y - (this.size.h - flower.height) / 2
      }
    }

    window.scrollTo(0, scrollToItem)
    filteredItems = []
  }

  resetFilter () {
    this.flowers.forEach((flower, i) => {
      flower.desopacifyFlower()
    })

    window.scrollTo(0, this.grid.startTop)
  }

  /**
   * gui function
   */
  gui () {
    this.guiInstance = new Gui()
    this.guiInstance.panel
    .addGroup({ label: 'Mask', enable: true })
    .addSlider(this.params.alphaMask, 'value', 'range', {
      label: 'Alpha',
      dp: 3,
      onChange: () => {
        this.flowers.forEach((flower, i) => flower.setAlphaMask(this.params.alphaMask.value))
      }
    })
    .addGroup({ label: 'Wind', enable: false })
    .addSlider(this.wind.force, 'value', 'range', { label: 'Force ', dp: 1 })
    .addSlider(this.wind.speed, 'value', 'range', { label: 'Speed', dp: 3 })
    .addGroup({ label: 'Debug', enable: false })
    .addCheckbox(this.params, 'debug', {
      label: 'Debug mode',
      onChange: () => {
        this.toggleDebugMode()
      }
    })
    .addCheckbox(this.params, 'toggleMask', {
      label: 'Toggle mask',
      onChange: () => {
        if (this.params.toggleMask) {
          this.flowers.forEach((flower, i) => {
            flower.createMask()
          })
          this.resize()
        } else {
          this.flowers.forEach((flower, i) => flower.removeMask())
        }
      }
    })
    .addCheckbox(this.params, 'toggleBackground', {
      label: 'Toggle black background',
      onChange: () => {
        if (this.params.toggleBackground) {
          this.container.style.backgroundColor = '#000'
        } else {
          this.container.style.backgroundColor = ''
        }
      }
    })
  }

  toggleDebugMode () {
    if (this.params.debug) {
      this.container.classList.add('debug')
    } else {
      this.container.classList.remove('debug')
    }
  }

  scroll (vars) {
    if (!this.isReady) return
    this.scrollY.dest = vars.target
    this.scrollY.current = Utils.round(vars.current)
    this.scrollY.offset = 0
  }

  resize (size) {
    let paddingtop = 50
    let paddingBottom = 100
    this.size = size
    if (PIXI.settings.RESOLUTION) {
      this.renderer.resize(this.size.w, this.size.h)
    } else {
      this.renderer.view.width = `${this.size.w}`
      this.renderer.view.height = `${this.size.h}`
    }
    this.renderer.view.style.width = `${this.size.w}px`
    this.renderer.view.style.height = `${this.size.h}px`

    if (this.isReady) {
      this.coverComp.resize(this.size)
      this.filtersComp.resize(this.size, this.coverComp.bounds.coverContainer)

      let item = 0
      let itemsByRow = this.grid.itemsByRow()
      this.grid.row = 0
      this.grid.currentCol = 0
      this.grid.startTop = 0
      this.grid.startTop = this.coverComp.offsetHeight + this.filtersComp.offsetHeight

      this.flowers.forEach((flower, i) => {
        if (item % this.grid.itemsByRow() === 0) {
          this.grid.row++
        }

        flower.resize(this.size, i, this.grid)

        item++

        if (i % (itemsByRow * 2) === 0 || i % (itemsByRow * 2) === itemsByRow) {
          this.grid.currentCol++
        } else {
          if (i % (itemsByRow * 2) < itemsByRow) {
            if ((i - 1) % 2) this.grid.currentCol++
            // console.log('model 1 : ', i, (i - 1) % 2)
          } else {
            if ((i) % 2) this.grid.currentCol++
            // console.log('model 2 : ', i, i % 2)
          }

          // console.log((i) % 2)
          // this.grid.currentCol++
        }

        if (this.grid.currentCol > this.grid.column() - 1) this.grid.currentCol = 0
      })

      this.offsetHeight = (this.grid.row) * (Math.min(this.size.h, this.grid.maxHeight())) + paddingtop + paddingBottom

      // let offsetFilters = this.filtersComp.offsetHeight
      let footerTop = this.coverComp.offsetHeight + this.filtersComp.offsetHeight + this.offsetHeight
      this.homeFooterComp.resize(this.size, footerTop)
    }
  }

  update (time) {
    if (this.isReady) {
      this.coverComp.update({time: time, scrollY: this.scrollY})
      this.filtersComp.update({time: time, scrollY: this.scrollY})

      this.scene.position.y = -this.scrollY.current
      // if (!this.filtersComp.lockPosition) {
      //
      //   if (this.filtersComp.addOffset) {
      //     this.scrollY.offset = this.filtersComp.offsetHeight
      //     this.scene.position.y += this.scrollY.offset
      //   }
      // } else {
      //   this.scene.position.y = -this.filtersComp.bounds.filtersContainer.top
      // }

      // this.flowerParticles.update(time)
      this.flowers.forEach((flower, i) => {
        flower.update({time: time, wind: this.wind, scrollY: this.scrollY})
      })

      this.homeFooterComp.update({time: time, scrollY: this.scrollY})
    }

    this.renderer.render(this.scene)
  }

  destroy () {
    this.removeListeners()
    this.guiInstance && this.guiInstance.destroyGui()
    // this.flowerParticles.destroy()
    this.flowers.forEach((flower, i) => flower.destroy())
    this.scene.destroy({
      children: true
    })
    this.renderer.destroy(true)
  }

}



/** WEBPACK FOOTER **
 ** ./src/components/Home/classes/Scene.js
 **/